import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Preferences } from '@capacitor/preferences';

import { androidAuthCallbackUrl } from '@/android-app-identity';

import { authClient } from './client';
import { setNativeSessionToken } from './capacitor-session';
import { isNativePlatform } from './is-native';
import {
  authErrorFromCallback,
  googleSignInBrowserUrl,
  isNativeAuthCallback,
  sessionTokenFromAuthCallback,
} from './native-auth-callback';
import { getApiBaseUrl } from '../env';
import { nativeAppStorageId } from '../native-app-id';

const CONSUMED_URL_KEY = 'couplespace_google_auth_callback';

type PendingSignIn = {
  resolve: (token: string) => void;
  reject: (error: Error) => void;
};

let pending: PendingSignIn | null = null;
let installed = false;

export function installNativeAuthDeepLink(): void {
  if (!isNativePlatform() || installed) return;
  installed = true;
  void App.addListener('appUrlOpen', (event) => {
    void receiveAuthCallback(event.url);
  });
  void App.getLaunchUrl().then((launch) => {
    // A sign-in already waiting for appUrlOpen must not be completed by an older launch URL.
    if (launch?.url && !pending) void receiveAuthCallback(launch.url);
  });
}

async function wasConsumed(url: string): Promise<boolean> {
  try {
    const { value } = await Preferences.get({ key: CONSUMED_URL_KEY });
    return value === url;
  } catch {
    return false;
  }
}

async function rememberConsumed(url: string): Promise<void> {
  try {
    await Preferences.set({ key: CONSUMED_URL_KEY, value: url });
  } catch {
    // A replay of this exact URL is the only thing this remembers.
  }
}

async function receiveAuthCallback(url: string): Promise<void> {
  if (!isNativeAuthCallback(url)) return;
  const token = sessionTokenFromAuthCallback(url);
  const authError = token ? null : authErrorFromCallback(url);
  if (await wasConsumed(url)) return;
  await rememberConsumed(url);

  if (pending) {
    const current = pending;
    if (token) current.resolve(token);
    else current.reject(new Error(authError ?? 'Google sign-in did not return a session'));
    return;
  }

  if (!token) return;
  await setNativeSessionToken(token);
  await authClient.getSession();
  window.location.assign('/app');
}

/** Google's browser redirect, then the session comes back into the app. Credential Manager is not used. */
export async function signInWithGoogleNative(): Promise<void> {
  installNativeAuthDeepLink();
  const callbackURL = androidAuthCallbackUrl(nativeAppStorageId());
  const { data, error } = await authClient.signIn.social({
    provider: 'google',
    callbackURL,
    errorCallbackURL: callbackURL,
    disableRedirect: true,
  });
  if (error) {
    throw new Error(error.message ?? 'Sign-in failed');
  }
  if (!data?.url) {
    throw new Error('Google sign-in did not return a URL');
  }

  const token = await openGoogleAndWait(googleSignInBrowserUrl(getApiBaseUrl(), data.url));
  await setNativeSessionToken(token);
  await authClient.getSession();
}

function openGoogleAndWait(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let browserListener: { remove: () => Promise<void> } | undefined;
    let settled = false;

    const settle = (fn: () => void) => {
      if (settled) return;
      settled = true;
      pending = null;
      if (timer) clearTimeout(timer);
      void browserListener?.remove();
      void Browser.close().catch(() => undefined);
      fn();
    };

    pending = {
      resolve: (token) => settle(() => resolve(token)),
      reject: (error) => settle(() => reject(error)),
    };

    void Browser.addListener('browserFinished', () => {
      timer = setTimeout(() => {
        settle(() => reject(new Error('Google sign-in was closed')));
      }, 2000);
    }).then((listener) => {
      browserListener = listener;
      if (settled) void listener.remove();
    });

    void Browser.open({ url }).catch((err: unknown) => {
      settle(() => reject(err instanceof Error ? err : new Error('Google sign-in failed')));
    });
  });
}
